import { createShareSchema, type ArticleSummary, type CreateShareInput, type Language, type ShareRecord, type ShareResponse } from '@rabbit/shared';
import { z } from 'zod';

const MIN_EXTRACT_LENGTH = 40;
const blockedTitle = /^(list of|lists of|outline of|index of|список |списки |category:|template:|portal:|wikipedia:|категория:|шаблон:|портал:|википедия:)/i;

async function requestJson<T>(url: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, headers: { 'content-type': 'application/json', ...init?.headers } });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(body?.message ?? `Request failed with status ${response.status}`);
  }
  return schema.parse(await response.json());
}

export async function getDiscover(lang: Language, signal?: AbortSignal): Promise<ArticleSummary[]> {
  const url = wikiActionUrl(lang, {
    generator: 'random',
    grnnamespace: '0',
    grnlimit: '35',
    prop: 'extracts|pageimages|pageterms|pageprops|info',
    exintro: '1',
    explaintext: '1',
    exchars: '900',
    piprop: 'thumbnail|original',
    pithumbsize: '1200',
    wbptterms: 'description',
    inprop: 'url',
  });
  const payload = await fetchJson(url, signal);
  return normalizeActionPages(payload, lang).sort((a, b) => qualityScore(b) - qualityScore(a)).slice(0, 10);
}

export async function getRelated(lang: Language, title: string, signal?: AbortSignal): Promise<ArticleSummary[]> {
  const items = await searchRestPages(lang, title, 30, signal);
  const source = title.toLocaleLowerCase();
  return items.filter((item) => item.title.toLocaleLowerCase() !== source).slice(0, 20);
}

export async function getArticles(lang: Language, titles: string[], signal?: AbortSignal): Promise<ArticleSummary[]> {
  const unique = [...new Set(titles.map((title) => title.trim()).filter(Boolean))].slice(0, 50);
  const results = await Promise.all(unique.map(async (title) => {
    const candidates = await searchRestPages(lang, title, 5, signal);
    return candidates.find((item) => item.title.toLocaleLowerCase() === title.toLocaleLowerCase()) ?? candidates[0];
  }));
  return results.filter((item): item is ArticleSummary => item !== undefined);
}

const shareResponseSchema = z.object({ id: z.string(), deepLink: z.string(), webLink: z.url() });
export async function createShare(input: CreateShareInput): Promise<ShareResponse> {
  return requestJson('/api/shares', shareResponseSchema, { method: 'POST', body: JSON.stringify(createShareSchema.parse(input)) });
}

const shareRecordSchema = z.object({ share: createShareSchema.and(z.object({ id: z.string(), createdAt: z.string(), expiresAt: z.string() })) });
export async function getShare(id: string): Promise<ShareRecord> {
  return (await requestJson(`/api/shares/${encodeURIComponent(id)}`, shareRecordSchema)).share;
}

export async function getRuntimeConfig(): Promise<{ defaultLanguage: Language }> {
  return requestJson('/api/config', z.object({ defaultLanguage: z.enum(['ru', 'en']) }));
}

function wikiActionUrl(lang: Language, parameters: Record<string, string>): string {
  const url = new URL(`https://${lang}.wikipedia.org/w/api.php`);
  for (const [key, value] of Object.entries({ action: 'query', format: 'json', formatversion: '2', origin: '*', ...parameters })) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

function wikiRestUrl(lang: Language, path: string, parameters: Record<string, string>): string {
  const url = new URL(`https://${lang}.wikipedia.org/w/rest.php/v1${path}`);
  for (const [key, value] of Object.entries(parameters)) url.searchParams.set(key, value);
  return url.toString();
}

async function fetchJson(url: string, signal?: AbortSignal): Promise<unknown> {
  const response = await fetch(url, { ...(signal ? { signal } : {}), headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(`Wikimedia returned HTTP ${response.status}`);
  return response.json() as Promise<unknown>;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function descriptionFrom(page: Record<string, unknown>): string | undefined {
  const direct = stringValue(page.description);
  if (direct) return direct;
  const terms = asRecord(page.terms)?.description;
  if (Array.isArray(terms)) return stringValue(terms[0]);
  return stringValue(terms);
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function absoluteWikiUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.startsWith('//')) return `https:${value}`;
  return value;
}

function normalizeActionPages(payload: unknown, lang: Language): ArticleSummary[] {
  const pages = asRecord(asRecord(payload)?.query)?.pages;
  const objectPages = asRecord(pages);
  const values = Array.isArray(pages) ? pages : objectPages ? Object.values(objectPages) : [];
  return values.flatMap((value) => {
    const page = asRecord(value);
    if (!page) return [];
    const id = typeof page.pageid === 'number' || typeof page.pageid === 'string' ? String(page.pageid) : undefined;
    const title = stringValue(page.title);
    const extract = stringValue(page.extract)?.replace(/\s+/g, ' ');
    if (!id || !title || !extract || page.ns !== 0 || extract.length < MIN_EXTRACT_LENGTH || blockedTitle.test(title) || asRecord(page.pageprops)?.disambiguation !== undefined) return [];
    const thumbnailUrl = absoluteWikiUrl(stringValue(asRecord(page.thumbnail)?.source));
    const originalImageUrl = absoluteWikiUrl(stringValue(asRecord(page.original)?.source));
    return [{
      id,
      lang,
      title,
      displayTitle: title,
      ...(descriptionFrom(page) ? { description: descriptionFrom(page) } : {}),
      extract,
      ...(thumbnailUrl ? { thumbnailUrl } : {}),
      ...(originalImageUrl ? { originalImageUrl } : {}),
      wikipediaUrl: stringValue(page.fullurl) ?? `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title.replaceAll(' ', '_'))}`,
    }];
  });
}

async function searchRestPages(lang: Language, query: string, limit: number, signal?: AbortSignal): Promise<ArticleSummary[]> {
  const payload = await fetchJson(wikiRestUrl(lang, '/search/page', { q: query, limit: String(limit) }), signal);
  const pages = asRecord(payload)?.pages;
  if (!Array.isArray(pages)) return [];
  return pages.flatMap((value) => {
    const page = asRecord(value);
    if (!page) return [];
    const id = typeof page.id === 'number' || typeof page.id === 'string' ? String(page.id) : undefined;
    const title = stringValue(page.title);
    const excerpt = stringValue(page.excerpt);
    const extract = excerpt ? stripHtml(excerpt) : descriptionFrom(page);
    if (!id || !title || !extract || extract.length < MIN_EXTRACT_LENGTH || blockedTitle.test(title)) return [];
    const key = stringValue(page.key) ?? title;
    const thumbnailUrl = absoluteWikiUrl(stringValue(asRecord(page.thumbnail)?.url));
    return [{
      id,
      lang,
      title,
      displayTitle: title,
      ...(descriptionFrom(page) ? { description: descriptionFrom(page) } : {}),
      extract,
      ...(thumbnailUrl ? { thumbnailUrl, originalImageUrl: thumbnailUrl } : {}),
      wikipediaUrl: `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(key.replaceAll(' ', '_'))}`,
    }];
  });
}

function qualityScore(article: ArticleSummary): number {
  return Math.min(article.extract.length, 900) / 90 + (article.description ? 5 : 0) + (article.thumbnailUrl ? 4 : 0);
}
